import { useState } from "react";
import { Plus, Trash2, Clock, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { MenusOutput, DayOfWeek, TimeInterval } from "@/interfaces/menus";

interface OperatingHoursSettingsProps {
  menu: MenusOutput;
  onMenuChange: (menu: MenusOutput) => void;
}

const dayNames = {
  0: "Segunda-feira",
  1: "Terça-feira",
  2: "Quarta-feira",
  3: "Quinta-feira",
  4: "Sexta-feira",
  5: "Sábado",
  6: "Domingo",
};

const dayNamesShort = {
  0: "Seg",
  1: "Ter",
  2: "Qua",
  3: "Qui",
  4: "Sex",
  5: "Sáb",
  6: "Dom",
};

export function OperatingHoursSettings({
  menu,
  onMenuChange,
}: OperatingHoursSettingsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | null>(null);
  const [intervals, setIntervals] = useState<TimeInterval[]>([]);

  const openDayEditor = (day: DayOfWeek) => {
    const existingDay = menu.operatingDays.find((od) => od.day === day);
    setSelectedDay(day);
    setIntervals(
      existingDay?.intervals || [{ startWork: "09:00", endWork: "18:00" }]
    );
    setIsDialogOpen(true);
  };

  const saveDaySchedule = () => {
    if (selectedDay === null) return;

    const filteredIntervals = intervals.filter(
      (interval) => interval.startWork && interval.endWork
    );

    // Remove o dia selecionado da lista existente para evitar duplicatas
    const updatedOperatingDays = menu.operatingDays.filter(
      (od) => od.day !== selectedDay
    );

    // Só adiciona o dia se houver intervalos válidos
    if (filteredIntervals.length > 0) {
      updatedOperatingDays.push({
        day: selectedDay,
        intervals: filteredIntervals,
      });
    }

    onMenuChange({
      ...menu,
      operatingDays: updatedOperatingDays.sort((a, b) => a.day - b.day),
    });

    setIsDialogOpen(false);
    setSelectedDay(null);
    setIntervals([]);
  };

  const copyToOtherDays = () => {
    if (selectedDay === null) return;

    const filteredIntervals = intervals.filter(
      (interval) => interval.startWork && interval.endWork
    );

    if (filteredIntervals.length === 0) return;

    // Lista de todos os dias da semana
    const allDays: DayOfWeek[] = [0, 1, 2, 3, 4, 5, 6] as DayOfWeek[];

    // Criar uma nova lista de operatingDays aplicando os horários para todos os dias
    const updatedOperatingDays = allDays.map((day) => ({
      day,
      intervals: [...filteredIntervals], // Criar uma cópia dos intervalos para cada dia
    }));

    onMenuChange({
      ...menu,
      operatingDays: updatedOperatingDays.sort((a, b) => a.day - b.day),
    });

    setIsDialogOpen(false);
    setSelectedDay(null);
    setIntervals([]);
  };

  const clearAllSchedules = () => {
    onMenuChange({
      ...menu,
      operatingDays: [],
    });
  };

  const addInterval = () => {
    setIntervals([...intervals, { startWork: "09:00", endWork: "18:00" }]);
  };

  const removeInterval = (index: number) => {
    setIntervals(intervals.filter((_, i) => i !== index));
  };

  const updateInterval = (
    index: number,
    field: keyof TimeInterval,
    value: string
  ) => {
    const updatedIntervals = [...intervals];
    updatedIntervals[index] = { ...updatedIntervals[index], [field]: value };
    setIntervals(updatedIntervals);
  };

  const isDayConfigured = (day: DayOfWeek) => {
    return menu.operatingDays.some((od) => od.day === day);
  };

  const getDaySchedule = (day: DayOfWeek) => {
    const operatingDay = menu.operatingDays.find((od) => od.day === day);
    if (!operatingDay) return null;

    return operatingDay.intervals
      .map((interval) => `${interval.startWork} - ${interval.endWork}`)
      .join(", ");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Configure os horários de funcionamento clicando nos dias da semana
        </div>
        {menu.operatingDays.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Limpar Todos
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Limpar todos os horários?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação irá remover todos os horários de funcionamento
                  configurados para todos os dias da semana. Esta ação não pode
                  ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={clearAllSchedules}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Limpar Todos
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {Object.entries(dayNamesShort).map(([dayNum, dayName]) => {
          const day = parseInt(dayNum) as DayOfWeek;
          const isConfigured = isDayConfigured(day);

          return (
            <Button
              key={day}
              variant={isConfigured ? "default" : "outline"}
              size="sm"
              onClick={() => openDayEditor(day)}
              className={`h-16 flex flex-col items-center justify-center p-2 ${
                isConfigured
                  ? "bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
                  : "hover:bg-gray-50"
              }`}
            >
              <span className="text-xs font-medium">{dayName}</span>
              {isConfigured && (
                <Badge
                  variant="secondary"
                  className="mt-1 text-xs bg-white/20 text-white border-none"
                >
                  <Clock className="h-3 w-3" />
                </Badge>
              )}
            </Button>
          );
        })}
      </div>

      {menu.operatingDays.length > 0 && (
        <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-sm text-gray-700">
            Horários Configurados:
          </h4>
          {menu.operatingDays.map((operatingDay) => (
            <div
              key={operatingDay.day}
              className="flex items-center justify-between text-sm"
            >
              <span className="font-medium">{dayNames[operatingDay.day]}:</span>
              <span className="text-gray-600">
                {getDaySchedule(operatingDay.day)}
              </span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Horários - {selectedDay !== null ? dayNames[selectedDay] : ""}
            </DialogTitle>
            <DialogDescription>
              Configure os horários de funcionamento para este dia. Você pode
              adicionar múltiplos períodos ou remover todos os horários para não
              atender neste dia.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {intervals.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
                <Clock className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">
                  Nenhum horário configurado para este dia.
                </p>
                <p className="text-xs mt-1">
                  Adicione um período ou este dia será removido.
                </p>
              </div>
            ) : (
              intervals.map((interval, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-3 border rounded-lg"
                >
                  <div className="flex-1">
                    <Label htmlFor={`start-${index}`} className="text-xs">
                      Abertura
                    </Label>
                    <Input
                      id={`start-${index}`}
                      type="time"
                      value={interval.startWork}
                      onChange={(e) =>
                        updateInterval(index, "startWork", e.target.value)
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor={`end-${index}`} className="text-xs">
                      Fechamento
                    </Label>
                    <Input
                      id={`end-${index}`}
                      type="time"
                      value={interval.endWork}
                      onChange={(e) =>
                        updateInterval(index, "endWork", e.target.value)
                      }
                      className="mt-1"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeInterval(index)}
                    className="mt-5 p-2"
                    title="Remover este período"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}

            <Button
              variant="outline"
              onClick={addInterval}
              className="w-full"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Período
            </Button>

            <Button
              variant="outline"
              onClick={copyToOtherDays}
              className="w-full mt-2"
              size="sm"
            >
              Copiar para outros dias
            </Button>

            <div className="flex gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={saveDaySchedule}
                className="flex-1 bg-[#EE7B11] hover:bg-[#d06a0a]"
              >
                {intervals.length === 0 ? "Remover" : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
